import { SUPPORTED_RESOLUTIONS_CONST } from '@ui/services/pyth-benchmark/types';
import { getChartTimezone } from '@ui/utils/getUtcOffset';
import { token } from 'styled-system/tokens';
import type {
  ChartingLibraryFeatureset,
  ChartingLibraryWidgetOptions,
  ColorTypes,
  ResolutionString,
} from 'trading-view';

const RED = token('colors.price.priceDown');
const GREEN = token('colors.price.priceUp');

const chartingLibraryPath = '/tradingview/charting_library/'; // must have trailing slash

export const chartStyleOverrides: { [key: string]: boolean | string } = [
  'candleStyle',
  'hollowCandleStyle',
  'haStyle',
].reduce((acc: { [key: string]: boolean | string }, cv) => {
  acc[`mainSeriesProperties.${cv}.drawWick`] = true;
  acc[`mainSeriesProperties.${cv}.drawBorder`] = false;
  acc[`mainSeriesProperties.${cv}.upColor`] = GREEN;
  acc[`mainSeriesProperties.${cv}.downColor`] = RED;
  acc[`mainSeriesProperties.${cv}.wickUpColor`] = GREEN;
  acc[`mainSeriesProperties.${cv}.wickDownColor`] = RED;
  acc[`mainSeriesProperties.${cv}.borderUpColor`] = GREEN;
  acc[`mainSeriesProperties.${cv}.borderDownColor`] = RED;

  return acc;
}, {});

export const chartOverrides = {
  'paneProperties.background': '#070606',
  'paneProperties.backgroundGradientStartColor': '#070606',
  'paneProperties.backgroundGradientEndColor': '#070606',
  'paneProperties.backgroundType': 'solid' as ColorTypes,
  'paneProperties.vertGridProperties.color': '#151414',
  'paneProperties.vertGridProperties.style': 0,
  'paneProperties.horzGridProperties.color': '#151414',
  'paneProperties.horzGridProperties.style': 0,
  // 'mainSeriesProperties.priceLineColor': '#3a3e5e', // should not override this , else semantic price color wont show (red/green)
  'scalesProperties.textColor': '#b0b2bb',
  'scalesProperties.lineColor': '#070606',
  ...chartStyleOverrides,
};

export const disabledFeaturesOnMobile: ChartingLibraryFeatureset[] = [];

export const disabledFeatures: ChartingLibraryFeatureset[] = [
  'header_saveload',
  'header_symbol_search',
  'header_compare',

  'create_volume_indicator_by_default',

  // Hide the symbol information since the data there is useless and we do not have the full info there
  'symbol_info_long_description',
  'symbol_search_hot_key',
  'symbol_info_price_source',
  'symbol_info',
];
const MUST_HAVE_FEATURES: ChartingLibraryFeatureset[] = [
  'iframe_loading_compatibility_mode', // MUST have, for metamask compatibility
];
export const enabledFeatures: ChartingLibraryFeatureset[] = [
  'side_toolbar_in_fullscreen_mode',
  'header_in_fullscreen_mode',
  'items_favoriting',
  'use_localstorage_for_settings',
  'hide_resolution_in_legend',
  'timezone_menu',
  'show_percent_option_for_right_margin',
  'timeframes_toolbar',
  'hide_object_tree_and_price_scale_exchange_label',
  ...MUST_HAVE_FEATURES,
];

export const enabledFeaturesOnMobile: ChartingLibraryFeatureset[] = [
  'hide_left_toolbar_by_default',
];

export const SUPPORTED_RESOLUTION_STRINGS = [
  ...SUPPORTED_RESOLUTIONS_CONST,
] as ResolutionString[];

export const defaultChartProps = {
  locale: 'en',

  timezone: getChartTimezone(),
  library_path: chartingLibraryPath,
  debug: false,
  theme: 'dark',
  // auto_save_delay: 2,
  interval: '5' as ResolutionString,
  fullscreen: false,
  autosize: true,
  client_id: 'tradingview.com',
  user_id: 'public_user_id',
  overrides: chartOverrides,
  enabled_features: enabledFeatures,
  disabled_features: disabledFeatures,
  custom_css_url: '/tradingview/tradingview-chart.css',
  loading_screen: { backgroundColor: '#0c0c0e', foregroundColor: '#c7f50d' },
  favorites: {
    intervals: SUPPORTED_RESOLUTION_STRINGS,
    chartTypes: ['Candles', 'Line'],
  },
  // FIXME: do we still need it?
  // custom_formatters: {
  //   timeFormatter: {
  //     format: (date: Date) => formatTVTime(date),
  //     formatLocal: function (date: Date): string {
  //       return formatTVTime(date);
  //     },
  //   },
  //   dateFormatter: {
  //     format: (date: Date) => formatTVDate(date),
  //     formatLocal: function (date: Date): string {
  //       return formatTVDate(date);
  //     },
  //   },
  // },
  time_frames: [
    {
      text: '6h',
      resolution: '5' as ResolutionString,
      description: '6 Hours',
    },
    {
      text: '1d',
      resolution: '15' as ResolutionString,
      description: '1 Day',
    },
    {
      text: '3d',
      resolution: '60' as ResolutionString,
      description: '3 Days',
    },
    {
      text: '2w',
      resolution: '240' as ResolutionString,
      description: '2 Weeks',
    },
    {
      text: '3m',
      resolution: '1D' as ResolutionString,
      description: '8 Months',
    },
  ],
} satisfies Omit<ChartingLibraryWidgetOptions, 'container' | 'datafeed'>;

export const simpleChartProps = {
  enabled_features: [...MUST_HAVE_FEATURES],
  disabled_features: [
    'context_menus',
    'edit_buttons_in_legend',
    'header_widget',
    'use_localstorage_for_settings',
    'adaptive_logo',
    'always_show_legend_values_on_mobile',
    'border_around_the_chart',
    'control_bar',
    'side_toolbar_in_fullscreen_mode',
    'header_in_fullscreen_mode',
    'items_favoriting',
    'use_localstorage_for_settings',
    'hide_resolution_in_legend',
    'timezone_menu',
    'show_percent_option_for_right_margin',
    'timeframes_toolbar',
    'hide_object_tree_and_price_scale_exchange_label',
    'header_saveload',
    'header_symbol_search',
    'header_compare',

    'create_volume_indicator_by_default',

    // Hide the symbol information since the data there is useless and we do not have the full info there
    'symbol_info_long_description',
    'symbol_search_hot_key',
    'symbol_info_price_source',
    'symbol_info',
    'display_market_status',
    'go_to_date',
    'header_saveload',
    'hide_main_series_symbol_from_indicator_legend',

    'items_favoriting',
    'left_toolbar',
    'legend_widget',
    'main_series_scale_menu',
    'object_tree_legend_mode',
    'popup_hints',
    'show_chart_property_page',
    'show_right_widgets_panel_by_default',
    'show_symbol_logo_in_legend',
    'show_object_tree',
    'symbol_info',
    'timeframes_toolbar',
    'timezone_menu',
    // 'chart_scroll', // enable scroll feature, to let user to see lines if not showing
    'chart_zoom',
    'horz_touch_drag_scroll',
  ],
} satisfies Omit<
  ChartingLibraryWidgetOptions,
  'container' | 'datafeed' | 'locale' | 'interval'
>;
